import { Dispatch, useMemo } from 'react';
import { SigninPopupArgs, SignoutRedirectArgs, UserManager } from 'oidc-client-ts';

import {
  Action,
  ActiveNavigator,
  AuthActions,
  AuthState,
  UserManagerContext,
} from '../auth-provider.types';
import { UNSUPPORTED_ENV } from '../constants/error.constant';

const userManagementKeys = [
  'clearStaleState',
  'querySessionStatus',
  'revokeTokens',
  'startSilentRenew',
  'signinSilentCallback',
  'stopSilentRenew',
  'signoutCallback',
  'removeUser',
  'getUser',
] as const;

/**
 * The navigators, as opposed to the plain pass-through methods above: each one
 * sends the user away from the app, so each is wrapped to report that a
 * navigation is in flight. No runtime list is needed any more — the wrappers
 * are written out individually so their signatures survive.
 */
type NavigatorKey =
  | ActiveNavigator.signinPopup
  | ActiveNavigator.signinSilent
  | ActiveNavigator.signinRedirect
  | ActiveNavigator.signoutPopup
  | ActiveNavigator.signoutRedirect;

const unsupportedEnvironment = (fnName: string) => () => {
  throw new Error(UNSUPPORTED_ENV(fnName));
};

/**
 * Supplies `id_token_hint` on sign-out. IdentityServer4 reads
 * `post_logout_redirect_uri` only when the end-session request also carries a
 * hint, so without one the user lands on the identity provider's fallback URL
 * instead of back in the app they logged out of. oidc-client-ts fills the hint
 * from its own user store, which can already be emptied by the time logout runs
 * — a 401 or a cross-app logout clears it — so we supply it from the provider's
 * state as well. A caller-supplied hint wins.
 */
const withIdTokenHint = (
  args: SignoutRedirectArgs | undefined,
  user: AuthState['user'],
): SignoutRedirectArgs | undefined => {
  if (!user?.id_token) return args;
  return { id_token_hint: user.id_token, ...args };
};

/** Hook that provides overrides with state management actions and returns a new api with the same names */
export const useUserManager = (
  userManager: UserManager,
  dispatch: Dispatch<Action>,
  state: AuthState,
  onRemoveUser?: () => void | Promise<void>,
) =>
  useMemo(() => {
    /**
     * Wraps one navigator call so the provider can dispatch a state change when the
     * navigation starts and when it settles. Generic in both the argument and the
     * result, so each navigator keeps its own signature: a mismatched argument shape
     * is a compile error rather than a value quietly dropped at runtime, and callers
     * of `signinSilent` still get `User | null` rather than a flattened union.
     */
    const navigatorFor = <TArgs, TResult>(
      method: NavigatorKey,
      call: (args?: TArgs) => Promise<TResult>,
    ) => {
      if (!userManager[method]) return unsupportedEnvironment(method);

      return async (args?: TArgs): Promise<TResult> => {
        dispatch({ type: AuthActions.NavigatorInit, method });
        try {
          return await call(args);
        } finally {
          dispatch({ type: AuthActions.NavigatorClosed });
        }
      };
    };

    // The caller's argument is forwarded as-is. It used to be taken as a rest parameter and
    // spread into an object literal, which turned it into index keys and silently dropped it.
    // Nothing of our own travels with it either: oidc-client-ts persists whatever it is handed
    // as `state_data` into the state store — localStorage by default — so passing AuthState
    // here wrote the user's access, id and refresh tokens to disk on every navigation.
    // Annotated rather than cast, so the contract is checked instead of asserted.
    const navigation: Pick<UserManagerContext, NavigatorKey> = {
      signinPopup: navigatorFor(ActiveNavigator.signinPopup, (args?: SigninPopupArgs) =>
        userManager.signinPopup(args),
      ),
      signinSilent: navigatorFor(ActiveNavigator.signinSilent, (args) =>
        userManager.signinSilent(args),
      ),
      signinRedirect: navigatorFor(ActiveNavigator.signinRedirect, (args) =>
        userManager.signinRedirect(args),
      ),
      signoutPopup: navigatorFor(ActiveNavigator.signoutPopup, (args) =>
        userManager.signoutPopup(args),
      ),
      signoutRedirect: navigatorFor(ActiveNavigator.signoutRedirect, (args) =>
        userManager.signoutRedirect(withIdTokenHint(args, state.user)),
      ),
    };

    // We still need to bind the rest of the functions that remains the same
    const userManagement = Object.fromEntries(
      userManagementKeys.map((key) => [
        key,
        userManager[key]?.bind(userManager) ?? unsupportedEnvironment(key),
      ]),
    ) as Pick<UserManagerContext, (typeof userManagementKeys)[number]>;

    // Manually creating override as
    const removeUser = async () => {
      await userManager.removeUser();
      onRemoveUser && (await onRemoveUser());
    };

    const getAccessToken = () => userManager.getUser().then((user) => user?.access_token);

    return Object.assign(
      { config: userManager.settings, events: userManager.events },
      userManagement,
      navigation,
      { getAccessToken, removeUser },
    ) as UserManagerContext;
  }, [dispatch, onRemoveUser, state.user, userManager]);
